import React, { useEffect, useMemo, useState } from 'react';
import { Container, Card, Row, Col, Form, Button, Badge, Spinner, Breadcrumb } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { API } from 'aws-amplify';
import * as queries from '../../graphql/queries';
import * as mutations from '../../graphql/mutations';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faChartLine, faCheck, faSave } from '@fortawesome/free-solid-svg-icons';
import { KPI_CATEGORIES, CREATE_YOUR_OWN } from '../../utils/kpiData';

/**
 * AddKpiPage — standalone "Add new KPI" page (project-scoped).
 * Mirrors the LeadershipFITT-style KPI picker used in ProjectView's KPI modal.
 */
const AddKpiPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [loadingProject, setLoadingProject] = useState(true);
  const [saving, setSaving] = useState(false);

  // Step 1: industry + base KPI selection
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedBase, setSelectedBase] = useState('');
  const [showMore, setShowMore] = useState(false);

  // Step 2: KPI fields
  const [form, setForm] = useState({
    title: '',
    xAxisLabel: 'Date',
    yAxisLabel: '',
    trend: true,
    minValue: '',
    maxValue: '',
    target: '',
    startDate: new Date(),
    endDate: new Date(),
  });

  useEffect(() => {
    const load = async () => {
      try {
        const res = await API.graphql({
          query: queries.getProject,
          variables: { id: projectId },
        });
        const p = res.data.getProject;
        setProject(p);
        // Default KPI dates to project dates if available
        if (p?.startDate) {
          setForm((prev) => ({
            ...prev,
            startDate: new Date(p.startDate),
            endDate: p.endDate ? new Date(p.endDate) : new Date(),
          }));
        }
      } catch (err) {
        console.error('Error loading project:', err);
      } finally {
        setLoadingProject(false);
      }
    };
    if (projectId) load();
  }, [projectId]);

  const category = useMemo(
    () => KPI_CATEGORIES.find((c) => c.id === selectedCategory),
    [selectedCategory]
  );

  const visibleKPIs = useMemo(() => {
    if (!category) return [];
    return showMore
      ? category.kpis
      : category.kpis.filter((k) => category.topFive.includes(k.id));
  }, [category, showMore]);

  const handleSelectBase = (kpi) => {
    setSelectedBase(kpi.id);
    if (kpi.id === CREATE_YOUR_OWN.id) {
      setForm((prev) => ({
        ...prev,
        title: '',
        yAxisLabel: '',
        trend: true,
        target: '',
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        title: kpi.name,
        yAxisLabel: kpi.abbreviation || '',
        trend: kpi.desiredTrend === '+',
      }));
    }
  };

  const canSave =
    !!selectedBase &&
    form.title.trim().length > 0 &&
    form.minValue !== '' &&
    form.maxValue !== '' &&
    form.target !== '' &&
    form.startDate &&
    form.endDate;

  const toIsoDate = (d) => {
    if (!d) return '';
    const dt = d instanceof Date ? d : new Date(d);
    if (isNaN(dt.getTime())) return '';
    return dt.toISOString().split('T')[0];
  };

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const input = {
        title: form.title.trim(),
        xAxisLabel: form.xAxisLabel?.trim() || 'Date',
        yAxisLabel: form.yAxisLabel?.trim() || '',
        trend: !!form.trend,
        // minValue / maxValue are stored locally but only sent once the backend
        // schema has been deployed with `amplify push`. Comment these back in
        // and add them to the graphql selection sets after deployment:
        // minValue: form.minValue !== '' ? parseFloat(form.minValue) : null,
        // maxValue: form.maxValue !== '' ? parseFloat(form.maxValue) : null,
        target: form.target !== '' && form.target !== null ? parseFloat(form.target) : null,
        startDate: new Date(form.startDate).toISOString(),
        endDate: new Date(form.endDate).toISOString(),
        projectID: projectId,
      };
      await API.graphql({
        query: mutations.createKPI,
        variables: { input },
      });
      navigate(`/project/${projectId}`);
    } catch (err) {
      console.error('Error creating KPI:', err);
      alert('Failed to create KPI. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loadingProject) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Breadcrumb>
        <Breadcrumb.Item onClick={() => navigate('/projects')} style={{ cursor: 'pointer' }}>
          Projects
        </Breadcrumb.Item>
        {project && (
          <Breadcrumb.Item
            onClick={() => navigate(`/project/${projectId}`)}
            style={{ cursor: 'pointer' }}
          >
            {project.name}
          </Breadcrumb.Item>
        )}
        <Breadcrumb.Item active>Add KPI</Breadcrumb.Item>
      </Breadcrumb>

      <div className="d-flex align-items-center justify-content-between mb-3">
        <h4 className="mb-0">
          <FontAwesomeIcon icon={faChartLine} className="me-2 text-success" />
          Add New KPI
        </h4>
        <Button variant="outline-secondary" size="sm" onClick={() => navigate(-1)}>
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back
        </Button>
      </div>

      <Card className="border-0 shadow-sm">
        <Card.Body className="p-4">
          {/* Step 1: Industry + KPI picker */}
          <div className="mb-4">
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Industry</Form.Label>
              <Form.Select
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setSelectedBase('');
                  setShowMore(false);
                }}
              >
                <option value="">— Select your industry —</option>
                {KPI_CATEGORIES.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {category && (
              <div>
                <Form.Label className="fw-semibold">Select a KPI</Form.Label>

                {/* Create Your Own */}
                <div
                  className={`d-flex align-items-center justify-content-between p-2 mb-1 rounded border ${
                    selectedBase === CREATE_YOUR_OWN.id
                      ? 'border-primary bg-primary bg-opacity-10'
                      : 'border-light'
                  }`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSelectBase(CREATE_YOUR_OWN)}
                >
                  <div>
                    <div className="fw-semibold text-primary">✏️ Create Your Own…</div>
                    <small className="text-muted">Define a custom KPI for this project</small>
                  </div>
                  {selectedBase === CREATE_YOUR_OWN.id && <Badge bg="primary">Selected</Badge>}
                </div>

                {/* Industry KPIs */}
                {visibleKPIs.map((kpi) => (
                  <div
                    key={kpi.id}
                    className={`d-flex align-items-center justify-content-between p-2 mb-1 rounded border ${
                      selectedBase === kpi.id
                        ? 'border-primary bg-primary bg-opacity-10'
                        : 'border-light'
                    }`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSelectBase(kpi)}
                  >
                    <div>
                      <div className="fw-semibold">
                        {kpi.name}{' '}
                        <span
                          className={`small ms-1 ${
                            kpi.desiredTrend === '+' ? 'text-success' : 'text-danger'
                          }`}
                        >
                          {kpi.desiredTrend === '+' ? '↑ Higher is better' : '↓ Lower is better'}
                        </span>
                      </div>
                      <small className="text-muted">
                        {kpi.abbreviation} — {kpi.definition}
                      </small>
                    </div>
                    {selectedBase === kpi.id && <Badge bg="primary">Selected</Badge>}
                  </div>
                ))}

                {category.kpis.length > 5 && (
                  <Button
                    variant="link"
                    size="sm"
                    className="mt-1 p-0"
                    onClick={() => setShowMore((s) => !s)}
                  >
                    {showMore ? '▲ Show less' : `▼ More… (${category.kpis.length - 5} more KPIs)`}
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Step 2: KPI fields */}
          {selectedBase && (
            <>
              <hr />
              <Form.Group className="mb-3">
                <Form.Label>KPI Title</Form.Label>
                <Form.Control
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                  placeholder="Enter KPI title"
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>X-Axis Label</Form.Label>
                    <Form.Control
                      type="text"
                      value={form.xAxisLabel}
                      onChange={(e) => setForm((p) => ({ ...p, xAxisLabel: e.target.value }))}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Y-Axis Label / Unit</Form.Label>
                    <Form.Control
                      type="text"
                      value={form.yAxisLabel}
                      onChange={(e) => setForm((p) => ({ ...p, yAxisLabel: e.target.value }))}
                      placeholder="e.g., %, $, count"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Target Value</Form.Label>
                    <Form.Control
                      type="number"
                      value={form.target}
                      onChange={(e) => setForm((p) => ({ ...p, target: e.target.value }))}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Desired Trend</Form.Label>
                    <div className="d-flex gap-3 pt-1">
                      <Form.Check
                        type="radio"
                        id="trend-up"
                        label="↑ Higher is better"
                        checked={form.trend === true}
                        onChange={() => setForm((p) => ({ ...p, trend: true }))}
                      />
                      <Form.Check
                        type="radio"
                        id="trend-down"
                        label="↓ Lower is better"
                        checked={form.trend === false}
                        onChange={() => setForm((p) => ({ ...p, trend: false }))}
                      />
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              {/* KPI Range Settings */}
              <div
                className="mb-3 p-3"
                style={{ backgroundColor: '#eaf4fb', border: '1px solid #bee3f8', borderRadius: 8 }}
              >
                <div className="d-flex align-items-center gap-2 mb-2">
                  <strong style={{ fontSize: '0.9rem' }}>KPI Range Settings (Required for Chart Display)</strong>
                  <span
                    title="Min and Max define the Y-axis range. Target is the goal line on the chart."
                    style={{ cursor: 'help', color: '#0d6efd', fontSize: '0.9rem' }}
                  >
                    ⓘ
                  </span>
                </div>
                <p className="text-muted mb-3" style={{ fontSize: '0.8rem' }}>Set the min, max, and target values.</p>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Minimum Value (Required) <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="number"
                        placeholder="e.g., 0"
                        value={form.minValue}
                        onChange={(e) => setForm((p) => ({ ...p, minValue: e.target.value }))}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Maximum Value (Required) <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="number"
                        placeholder="e.g., 100"
                        value={form.maxValue}
                        onChange={(e) => setForm((p) => ({ ...p, maxValue: e.target.value }))}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>
                        Target Value (Required) <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="number"
                        placeholder="e.g., 80"
                        value={form.target}
                        onChange={(e) => setForm((p) => ({ ...p, target: e.target.value }))}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={toIsoDate(form.startDate)}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          startDate: new Date(e.target.value + 'T00:00:00'),
                        }))
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={toIsoDate(form.endDate)}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          endDate: new Date(e.target.value + 'T00:00:00'),
                        }))
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}

          <div className="d-flex justify-content-end gap-2 mt-3">
            <Button variant="secondary" onClick={() => navigate(-1)} disabled={saving}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={!canSave || saving}>
              {saving ? (
                <>
                  <Spinner size="sm" animation="border" className="me-2" /> Saving…
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faSave} className="me-2" /> Create KPI
                </>
              )}
            </Button>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default AddKpiPage;
